import { useMemo, useRef } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import {
  Box,
  CircularProgress,
  Container,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import OrganizationForm, { OrganizationFormRef } from '@/content/components/common/Form/OrganizationForm';
import Button from '@/content/components/common/Button';
import { ToastType, useToast } from '@/contexts/ToastContext';
import { withSafeResultAsync } from '@/utils/helpers/safeResult';
import {
  useGetOrganizationDetailsByOrgUuid,
  usePatchUpdateOrganization,
} from '@/services/organizations/useOrganizations';
import { getCurrentOrganizationUuid } from '@/utils/helpers/authHelpers';
import useAuthorization from '@/hooks/useAuthorization';

const OrganizationSettings = () => {
  const { t } = useTranslation('common');
  const { t: tOrg } = useTranslation('organization');
  const { toastNotify } = useToast();
  const orgUuid = getCurrentOrganizationUuid();
  const organizationFormRef = useRef<OrganizationFormRef>(null);

  const { data: organizationDetails, isLoading } = useGetOrganizationDetailsByOrgUuid(orgUuid);
  const {
    mutateAsync: updateOrganization,
    isPending: isUpdatingOrganization,
  } = usePatchUpdateOrganization();

  const { canPerformAction } = useAuthorization();
  const organizationFieldsContext = useMemo(() => ({
    organizationUuid: orgUuid,
    viewerOrganizationUuid: orgUuid,
  }), [orgUuid]);
  const canUpdateOrganizationSettings = canPerformAction(
    'updateOrganizationSettings',
    organizationFieldsContext,
  );
  const canUpdateOrganizationBilling = canPerformAction(
    'updateOrganizationBilling',
    organizationFieldsContext,
  );

  const handleUpdateOrganization = async () => {
    const submitForm = await organizationFormRef.current?.submitForm();
    const formData = submitForm?.formData;
    const hasFormErrors = submitForm?.hasErrors;

    if (!formData) return;
    if (hasFormErrors) {
      toastNotify(ToastType.ERROR, tOrg('ORGANIZATION_SETTING_CONSTANTS.FORM_ERRORS'));
      return;
    }

    const result = await withSafeResultAsync(() => updateOrganization({
      orgUuid,
      name: formData.name,
      ...(canUpdateOrganizationBilling ? {
        billingAddress: {
          country: formData.country,
          street: formData.street,
          province: formData.province,
          postalCode: formData.postal,
        },
      } : {}),
    }));

    if (!result.ok) return;

    toastNotify(ToastType.SUCCESS, tOrg('ORGANIZATION_SETTING_CONSTANTS.UPDATE_SUCCESS'));
  };

  return (
    <>
      <Helmet>
        <title>{t('PAGE_META.ORGANIZATION_SETTINGS.TITLE')}</title>
      </Helmet>
      <Container maxWidth="sm" sx={{ py: 4 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress aria-label={t('ACCESSIBILITY.LOADING')} />
          </Box>
        ) : (
          <>
            <OrganizationForm
              ref={organizationFormRef}
              variant="update"
              organizationDetails={organizationDetails}
            />
            <Box sx={{ pt: 2 }}>
              <Button
                variant="contained"
                onClick={handleUpdateOrganization}
                disabled={isUpdatingOrganization || !canUpdateOrganizationSettings}
                isLoading={isUpdatingOrganization}
                sx={{ width: '100%' }}
              >
                {tOrg('ORGANIZATION_SETTING_CONSTANTS.UPDATE_ORGANIZATION_SETTINGS_BTN')}
              </Button>
            </Box>
          </>
        )}
      </Container>
    </>
  );
};

export default OrganizationSettings;
